import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useNavigationBlocker } from '@/components/routing/NavigationBlockerProvider';
import { useSubjectDocumentsRefresh } from '@/hooks/useDocumentRefresh';
import { useIntakeAutoSave } from '@/hooks/useIntakeAutoSave';
import { useIntakePage } from '@/hooks/useIntakePage';
import {
  useCancelScheduleMutation,
  useGenerateIntakeSummaryMutation,
  useGetIntakeMetadataQuery,
  useScheduleCallbackMutation,
  useUpdateIntakeMutation,
} from '@/services/api/intakeService';
import { ScheduleCallbackPayload } from '@/services/types/intake-types';

import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { IntakeAssignmentPanel } from './components/IntakeAssignmentPanel';
import { LeadSourceSelect } from './components/LeadSourceSelect';
import { ManualQualificationControl } from './components/ManualQualificationControl';
import MetadataPanel from './components/MetadataPanel';
import { ReferralSourceSelect } from './components/ReferralSourceSelect';
import { ScheduleCallbackPanel } from './components/ScheduleCallbackPanel';
import SectionNavigation from './components/SectionNavigation';
import StatusTrackingPanel from './components/StatusTrackingPanel';
import { SECTION_LABELS, SectionKey } from './constants';
import { hasAnyNeedsMoreDetail } from './evidence-review-dialog/statusHelpers';
import { convertFrontendToBackend, isSummaryComplete } from './form/formatter';
import { IntakeHeader } from './IntakeHeader';
import { IntakeSection } from './IntakeSection';
import { SendEngagementLetterDialog } from './SendEngagementLetterDialog';
import {
  InlineReviewPanel,
  IntakeStepper,
  SummaryAndRecommendationPanel,
} from './workflow';

interface IntakeFormProps {
  intakeId?: string | null;
  isNewIntake?: boolean;
}

const IntakeForm: React.FC<IntakeFormProps> = ({
  intakeId,
  isNewIntake = false,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Central page hook consolidating business logic
  const {
    intake: {
      form,
      status,
      handler,
      matterId,
      matterStatus,
      firmId,
      intakeStatuses,
      isLoading: isLoadingIntake,
      isSaving,
      isUpdatingNotes,
      isFormHydrated,
      error: intakeError,
      handleFieldChange,
      handleStatusChange,
      handleSave,
      handleAutoSave,
      selectedFirm,
      handleFirmChange,
      creatorName,
      assignedTo,
      handleAssignedToChange,
      handleGeneralNotesChange,
      addGeneralNote,
      handleEvidenceStatusesChange,
      companionRoot,
      isCompanionPrimary,
      summary,
      setSummary,
      handleCompanionsChange,
      handleLeadSourceChange,
      manualQualification,
      rejectionEmailSent,
      isScheduled,
      scheduledAt,
      scheduledAssigneeId,
      scheduledAssigneeName,
      scheduledBackupAssigneeId,
      scheduledBackupAssigneeName,
      googleCalendarEventId,
      googleCalendarEventLink,
      linkedLeadStatus,
    },
    exportHelpers: {
      canExport,
      canDownload,
      isProcessing,
      exportFailed,
      downloadTimestamp,
      handleStartExport,
      handleDownload,
    },
    loeDocumentId,
    buildInitialMergeFields,
    handleEngagementSent,
    archiveHelpers: { isArchived, isArchiving, handleArchiveToggle },
    conversion,
    refetch,
  } = useIntakePage(intakeId ?? null, isNewIntake);

  // Auto-save functionality - memoize to prevent unnecessary hook triggers
  const autoSaveData = useMemo(
    () => ({
      form,
      intakeStatuses,
      selectedFirm,
      ...(assignedTo && { assignedTo }),
      summary,
    }),
    [form, intakeStatuses, selectedFirm, assignedTo, summary]
  );

  const {
    isAutoSaving,
    lastSavedAt,
    autoSaveError,
    hasUnsavedChanges,
    errorCount,
    saveNow,
    cancelPendingSave,
  } = useIntakeAutoSave({
    formData: autoSaveData,
    onSave: handleAutoSave,
    isSavingExternally: isSaving,
    isNoteSaving: isUpdatingNotes,
    isLoading: isLoadingIntake,
    isFormHydrated,
    debounceMs: 5000, // Save 5 seconds after user stops typing (backup)
    intervalMs: 30000, // Also save every 30 seconds if there are changes
  });

  // Block navigation when there are unsaved changes
  const { setHasUnsavedChanges } = useNavigationBlocker('intake-form');
  
  // Sync unsaved changes state with the blocker
  useEffect(() => {
    setHasUnsavedChanges(
      hasUnsavedChanges,
      'You have unsaved changes on this intake form. Are you sure you want to leave?'
    );
  }, [hasUnsavedChanges, setHasUnsavedChanges]);

  const { data: timelineMeta = {} } = useGetIntakeMetadataQuery(
    intakeId ?? '',
    { skip: !intakeId },
  );

  // Get initial section from URL params or default to 'personalInfo'
  const initialSection =
    (searchParams.get('section') as SectionKey) || 'personalInfo';
  const isValidSection = Object.keys(SECTION_LABELS).includes(initialSection);

  // Local UI state
  const [activeSection, setActiveSection] = useState<SectionKey>(
    isValidSection ? initialSection : 'personalInfo',
  );

  // Stage state: 'collect' | 'review' | 'summary'
  const [activeStage, setActiveStage] = useState<
    'collect' | 'review' | 'summary'
  >('collect');

  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [generateIntakeSummary] = useGenerateIntakeSummaryMutation();
  const [updateIntake] = useUpdateIntakeMutation();
  const [scheduleCallback] = useScheduleCallbackMutation();
  const [cancelSchedule] = useCancelScheduleMutation();

  // Engagement letter dialog state
  const [showEngagementDialog, setShowEngagementDialog] = useState(false);
  const [engagementDialogCancelPrev, setEngagementDialogCancelPrev] =
    useState<boolean>(false);
  const isSaveNoteInteractionRef = useRef(false);
  const saveNoteInteractionTimeoutRef = useRef<number | null>(null);

  useSubjectDocumentsRefresh(intakeId, 'intake', loeDocumentId, 15000);

  const markSaveNoteInteraction = useCallback(() => {
    isSaveNoteInteractionRef.current = true;

    if (saveNoteInteractionTimeoutRef.current !== null) {
      window.clearTimeout(saveNoteInteractionTimeoutRef.current);
    }

    // Safety net: if click is canceled (mousedown/touchstart without click),
    // the guard auto-resets and cannot disable autosave indefinitely.
    saveNoteInteractionTimeoutRef.current = window.setTimeout(() => {
      isSaveNoteInteractionRef.current = false;
      saveNoteInteractionTimeoutRef.current = null;
    }, 800);
  }, []);

  const clearSaveNoteInteraction = useCallback(() => {
    if (saveNoteInteractionTimeoutRef.current !== null) {
      window.clearTimeout(saveNoteInteractionTimeoutRef.current);
      saveNoteInteractionTimeoutRef.current = null;
    }

    window.setTimeout(() => {
      isSaveNoteInteractionRef.current = false;
    }, 0);
  }, []);

  useEffect(
    () => () => {
      if (saveNoteInteractionTimeoutRef.current !== null) {
        window.clearTimeout(saveNoteInteractionTimeoutRef.current);
      }
    },
    [],
  );

  const handleBlurSave = useCallback(
    (event: React.FocusEvent<HTMLElement>) => {
      const nextFocusTarget = event.relatedTarget as HTMLElement | null;

      if (isSaveNoteInteractionRef.current) {
        return;
      }

      if (nextFocusTarget?.hasAttribute('data-save-note-button')) {
        return;
      }

      if (nextFocusTarget) {
        if (!event.currentTarget.contains(nextFocusTarget)) {
          saveNow().catch(() => {
            // Autosave errors are surfaced via hook state/toasts.
          });
        }
        return;
      }

      const container = event.currentTarget;
      window.requestAnimationFrame(() => {
        const activeElement = document.activeElement as HTMLElement | null;

        if (isSaveNoteInteractionRef.current) {
          return;
        }

        if (activeElement?.hasAttribute('data-save-note-button')) {
          return;
        }

        if (!container.contains(activeElement)) {
          saveNow().catch(() => {
            // Autosave errors are surfaced via hook state/toasts.
          });
        }
      });
    },
    [saveNow],
  );

  // Update URL when section changes
  useEffect(() => {
    if (activeSection) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('section', activeSection);
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [activeSection, searchParams, setSearchParams]);

  // Title helper
  const computePageTitle = (): string => {
    if (isNewIntake) return 'New Intake';
    if (form.personalInfo.fullName)
      return `${form.personalInfo.fullName} Intake`;
    return 'Edit Intake';
  };
  const pageTitle = computePageTitle();

  // Pre-export checks (review + summary)
  const needsReviewAttention = hasAnyNeedsMoreDetail(form.evidenceStatuses);
  const isSummaryReady = isSummaryComplete(summary);

  const handleGenerateSummary = async (): Promise<void> => {
    if (!intakeId) return;
    setIsGeneratingSummary(true);
    try {
      const generated = await generateIntakeSummary(intakeId).unwrap();
      setSummary(generated);
      setActiveStage('summary');
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  // Show loading state while fetching intake data
  if (isLoadingIntake && !isNewIntake) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading intake data...</p>
        </div>
      </div>
    );
  }

  // Show error state if loading failed
  if (intakeError && !isNewIntake) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <p className="font-medium">Failed to load intake data</p>
            <p className="text-sm text-gray-600 mt-1">
              Please try refreshing the page
            </p>
          </div>
          <Button
            type="button"
            onClick={() => window.location.reload()}
            variant="default"
          >
            Refresh Page
          </Button>
        </div>
      </div>
    );
  }

  if (!intakeId) {
    return null;
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="flex h-full">
        {/* Main Content Area */}
        <div className="flex-1 min-w-0 overflow-x-hidden">
          {/* Sticky Header + Stepper */}
          <div className="sticky top-0 z-20 bg-neutral-50">
            <div className="max-w-7xl mx-auto pt-2 px-3 sm:px-4 lg:px-6">
              {/* Header */}
              <div className="mb-2">
                <IntakeHeader
                  title={pageTitle}
                  intakeId={intakeId}
                  status={status}
                  handler={handler}
                  onSave={handleSave}
                  isSaving={isSaving}
                  matterId={matterId}
                  matterStatus={matterStatus}
                  isNew={isNewIntake}
                  canExport={canExport}
                  isProcessing={isProcessing}
                  exportFailed={exportFailed}
                  downloadTimestamp={downloadTimestamp}
                  handleStartExport={handleStartExport}
                  canDownload={canDownload}
                  handleDownload={handleDownload}
                  canConvert={conversion.canConvert}
                  isConverting={conversion.isConverting}
                  handleConvert={conversion.handleConvertToMatter}
                  isArchived={isArchived}
                  isArchiving={isArchiving}
                  handleArchiveToggle={handleArchiveToggle}
                  isAutoSaving={isAutoSaving}
                  lastAutoSaveTime={lastSavedAt}
                  autoSaveError={autoSaveError}
                  hasUnsavedChanges={hasUnsavedChanges}
                  errorCount={errorCount}
                  preexportNeeds={{
                    needsReview: needsReviewAttention,
                    needsSummary: !isSummaryReady,
                  }}
                  onGoToStage={(stage) => setActiveStage(stage)}
                  isLeadSourceMissing={conversion.isLeadSourceMissing}
                  isReferralSourceMissing={conversion.isReferralSourceMissing}
                  isRejectedOrUnqualified={conversion.isRejectedOrUnqualified}
                  hasLinkedLead={conversion.hasLinkedLead}
                  linkedLeadStatus={linkedLeadStatus}
                />
              </div>

              {/* Stage Stepper */}
              <div className="pb-3">
                <IntakeStepper
                  activeStage={activeStage}
                  onChange={setActiveStage}
                />
              </div>
            </div>
          </div>
          {/* End Sticky Header + Stepper */}

          <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 pb-2">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
              {/* Main Content */}
              <div className="lg:col-span-3">
                {activeStage === 'collect' && (
                  <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden min-h-[680px]">
                    <div className="sticky top-0 z-10 px-5 pt-4 pb-2 bg-white border-b border-gray-100">
                      <SectionNavigation
                        activeSection={activeSection}
                        onSectionChange={setActiveSection}
                      />
                    </div>
                    <div
                      className="px-5 py-4 space-y-3"
                      onBlur={handleBlurSave}
                    >
                      <IntakeSection
                        section={activeSection}
                        form={form}
                        intakeId={intakeId}
                        onFieldChange={handleFieldChange}
                        onCompanionsChange={handleCompanionsChange}
                        companionRoot={companionRoot}
                        isCompanionPrimary={isCompanionPrimary}
                      />
                    </div>
                  </div>
                )}
                {activeStage === 'review' && (
                  <div
                    onBlur={handleBlurSave}
                  >
                    <InlineReviewPanel
                      form={form}
                      initialStatuses={form.evidenceStatuses}
                      onChange={handleEvidenceStatusesChange}
                    />
                  </div>
                )}
                {activeStage === 'summary' && (
                  <div
                    onBlur={handleBlurSave}
                  >
                    <SummaryAndRecommendationPanel
                      data={summary}
                      onChange={setSummary}
                      isLoading={isGeneratingSummary}
                      onGenerateSummary={handleGenerateSummary}
                    />
                  </div>
                )}
              </div>

              {/* Metadata & Status Panel */}
              <div className="lg:col-span-1">
                <div
                  className="sticky top-1 space-y-2"
                  onBlur={handleBlurSave}
                >
                  <ManualQualificationControl
                    intakeId={intakeId}
                    value={manualQualification}
                    rejectionReason={intakeStatuses.rejectionReason}
                    rejectionEmailSent={rejectionEmailSent}
                    onUpdateSuccess={refetch}
                  />
                  <LeadSourceSelect
                    value={form.leadSource}
                    onChange={handleLeadSourceChange}
                    showError={conversion.isLeadSourceMissing}
                  />
                  <div
                    className={`bg-white rounded-lg shadow-sm p-3 border-2 transition-colors ${
                      conversion.isReferralSourceMissing
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <ReferralSourceSelect
                      value={form.referralSource.description}
                      onChange={(value) =>
                        handleFieldChange(
                          'referralSource',
                          'description',
                          value,
                        )
                      }
                      showError={conversion.isReferralSourceMissing}
                    />
                  </div>
                  <MetadataPanel
                    metadata={[
                      { label: 'Creator', value: creatorName || null },
                      {
                        label: 'Intake Created',
                        value: timelineMeta.intake_created_at || null,
                        date: true,
                      },
                      {
                        label: 'Matter Created',
                        value: timelineMeta.matter_created_at || null,
                        date: true,
                      },
                      {
                        label: 'LOE Sent',
                        value: timelineMeta.loe_sent_at || null,
                        date: true,
                      },
                      {
                        label: 'LOE Signed',
                        value: timelineMeta.loe_signed_at || null,
                        date: true,
                      },
                      {
                        label: 'Last Updated',
                        value: timelineMeta.intake_updated_at || null,
                        date: true,
                      },
                    ]}
                  />

                  {/* Intake Assignment Panel */}
                  <IntakeAssignmentPanel
                    selectedFirm={selectedFirm}
                    onFirmChange={handleFirmChange}
                    assignedTo={assignedTo}
                    onAssignedToChange={handleAssignedToChange}
                  />

                  <StatusTrackingPanel
                    intakeId={intakeId || ''}
                    statuses={intakeStatuses}
                    onStatusChange={handleStatusChange}
                    clientContactInfo={{
                      phone: form.personalInfo.phone,
                    }}
                    onSendEngagementLetter={(opts) => {
                      setEngagementDialogCancelPrev(
                        Boolean(opts?.cancelPrevious),
                      );
                      setShowEngagementDialog(true);
                    }}
                    intakeData={form}
                    onSendSuccess={refetch}
                    onSave={handleSave}
                  />

                  {/* Scheduled Callback Panel */}
                  {intakeId && (
                    <ScheduleCallbackPanel
                      clientState={form.incidentInfo?.state || ''}
                      intakeAssignedTo={assignedTo}
                      scheduledAt={scheduledAt}
                      scheduledAssigneeName={scheduledAssigneeName}
                      scheduledBackupAssigneeName={scheduledBackupAssigneeName}
                      scheduledAssigneeId={scheduledAssigneeId}
                      scheduledBackupAssigneeId={scheduledBackupAssigneeId}
                      googleCalendarEventId={googleCalendarEventId}
                      googleCalendarEventLink={googleCalendarEventLink}
                      onSchedule={async (data) => {
                        await scheduleCallback({
                          intakeId,
                          ...data,
                        } as ScheduleCallbackPayload).unwrap();
                      }}
                      onCancel={async () => {
                        await cancelSchedule(intakeId).unwrap();
                      }}
                    />
                  )}

                  {/* Intake Notes Section */}
                  <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm mt-3">
                    <Label
                      htmlFor="intake-notes"
                      className="text-sm font-semibold text-gray-900 mb-1 block"
                    >
                      Intake Notes
                    </Label>
                    <div className="flex flex-col gap-2">
                      <Textarea
                        id="intake-notes"
                        value={form.generalNotes}
                        onChange={(e) =>
                          handleGeneralNotesChange(e.target.value)
                        }
                        onBlur={handleBlurSave}
                        placeholder="Type a note and press Save to append…"
                        rows={4}
                        className="w-full text-sm resize-y"
                        aria-label="Intake notes"
                      />
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-400" aria-live="polite">
                          This note is internal-only and not included in shared
                          PDFs.
                        </p>
                        <Button
                          type="button"
                          variant="default"
                          size="sm"
                          data-save-note-button
                          disabled={!form.generalNotes?.trim()}
                          onClick={() => {
                            markSaveNoteInteraction();
                            // Cancel any pending auto-save SYNCHRONOUSLY before
                            // dispatching the notes mutation.  Without this, a
                            // debounce timer that fires in the same macrotask as
                            // this click can send stale general_notes_log to the
                            // server, overwriting the note we're about to save.
                            cancelPendingSave();
                            addGeneralNote(form.generalNotes).finally(
                              clearSaveNoteInteraction,
                            );
                          }}
                          onMouseDown={() => {
                            markSaveNoteInteraction();
                          }}
                          onTouchStart={() => {
                            markSaveNoteInteraction();
                          }}
                        >
                          Save Note
                        </Button>
                      </div>
                    </div>

                    {/* Previous Notes */}
                    <div className="mt-3">
                      <p className="text-sm font-semibold text-gray-900 mb-2">
                        Previous Notes
                      </p>
                      {/* Structured timeline */}
                      {form.generalNotesLog &&
                        form.generalNotesLog.length > 0 && (
                          <div className="space-y-2 mb-2">
                            {[...form.generalNotesLog]
                              .sort((a, b) => b.at.localeCompare(a.at))
                              .map((n, idx) => (
                                <div
                                  key={`${n.at}-${idx}`}
                                  className="border border-slate-200 rounded-md p-2 bg-slate-50"
                                >
                                  <div className="text-xs text-gray-500 mb-1">
                                    {new Date(n.at).toLocaleString()}{' '}
                                    {n.authorName ? `· ${n.authorName}` : ''}
                                  </div>
                                  <div className="text-sm text-gray-900 whitespace-pre-wrap">
                                    {n.text}
                                  </div>
                                </div>
                              ))}
                          </div>
                        )}
                      {/* Always show legacy if present */}
                      {form.generalNotesLegacy && (
                        <div className="space-y-2">
                          <div className="border border-slate-200 rounded-md p-2 bg-slate-50">
                            <div className="text-xs text-gray-500 mb-1">
                              Previous Notes (Legacy)
                            </div>
                            <div className="text-sm text-gray-900 whitespace-pre-wrap">
                              {form.generalNotesLegacy}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Engagement Letter Dialog */}
      {intakeId && (
        <SendEngagementLetterDialog
          intakeId={intakeId}
          firmId={firmId || ''}
          initialMergeFields={buildInitialMergeFields(
            convertFrontendToBackend(form, intakeStatuses),
          )}
          prevFormData={convertFrontendToBackend(form, intakeStatuses)}
          open={showEngagementDialog}
          onClose={() => setShowEngagementDialog(false)}
          onSent={handleEngagementSent}
          cancelPrevious={engagementDialogCancelPrev}
        />
      )}
    </div>
  );
};

export default IntakeForm;
